import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/store/authStore'
import { useTranslation } from 'react-i18next'
import { showSuccessToast, showErrorToast } from '@/utils/error-handler'
import { Separator } from '@/components/ui/separator'
import { Bot, Settings2, Monitor, Moon, Sun } from 'lucide-react'
import AIConfigModal from '@/components/ai/AIConfigModal'
import { isAIConfigured } from '@/services/ai/ai-config'
import { useTheme } from '@/hooks/useTheme'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export default function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { theme, setTheme } = useTheme()
  const [isSaving, setIsSaving] = useState(false)
  const [aiConfigOpen, setAiConfigOpen] = useState(false)
  const [aiConfigured, setAiConfigured] = useState(isAIConfigured())

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // TODO: Implement settings save
      await new Promise((resolve) => setTimeout(resolve, 500))
      showSuccessToast(t('settings.saveSuccess'))
    } catch (error) {
      showErrorToast(error, t('errors.generic'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('nav.settings')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('settings.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.profile')}</CardTitle>
            <CardDescription>{t('settings.profileDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('auth.fullName')}</Label>
              <Input
                id="fullName"
                type="text"
                placeholder={t('settings.fullNamePlaceholder')}
                defaultValue={user?.fullName || ''}
              />
            </div>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? t('common.loading') : t('common.save')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings.preferences')}</CardTitle>
            <CardDescription>{t('settings.preferencesDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">{t('settings.language')}</Label>
              <select
                id="language"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled
              >
                <option value="en">English</option>
              </select>
              <p className="text-xs text-muted-foreground">
                {t('settings.languageNote')}
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="theme-select">{t('settings.theme')}</Label>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger id="theme-select">
                  <SelectValue placeholder={t('settings.theme')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      {t('settings.themeLight')}
                    </div>
                  </SelectItem>
                  <SelectItem value="dark">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      {t('settings.themeDark')}
                    </div>
                  </SelectItem>
                  <SelectItem value="system">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      {t('settings.themeSystem')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t('settings.themeDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              AI Configuration
            </CardTitle>
            <CardDescription>
              Configure your AI API key to enable AI features. Your API key is stored locally in your browser's localStorage and never sent to our servers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-md">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${aiConfigured ? 'bg-green-50 text-green-600' : 'bg-muted'}`}>
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold">AI API Key</h4>
                  <p className="text-sm text-muted-foreground">
                    {aiConfigured 
                      ? 'AI is configured and ready to use'
                      : 'Configure your API key to enable AI features'
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant={aiConfigured ? 'outline' : 'default'}
                onClick={() => {
                  setAiConfigOpen(true)
                }}
              >
                <Settings2 className="w-4 h-4 mr-2" />
                {aiConfigured ? 'Update' : 'Configure'}
              </Button>
            </div>
            <div className="space-y-2 p-3 bg-muted/30 rounded-md border">
              <p className="text-xs font-medium">How it works:</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>API keys are stored in your browser's localStorage (not on our servers)</li>
                <li>All AI requests are made directly from your browser to OpenAI/Anthropic</li>
                <li>You control your own API keys and usage costs</li>
                <li>Keys are specific to this browser and device</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                <strong>Supported providers:</strong> OpenAI (GPT-4, GPT-3.5) and Anthropic (Claude)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-destructive">{t('settings.dangerZone')}</CardTitle>
            <CardDescription>{t('settings.dangerZoneDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-md">
              <div>
                <h4 className="font-semibold">{t('settings.deleteAccount')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('settings.deleteAccountDescription')}
                </p>
              </div>
              <Button variant="destructive" disabled>
                {t('settings.deleteAccount')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AIConfigModal 
        open={aiConfigOpen} 
        onOpenChange={(open) => {
          setAiConfigOpen(open)
          // Refresh configured status when modal closes
          if (!open) {
            setAiConfigured(isAIConfigured())
          }
        }} 
      />
    </div>
  )
}
